import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, BookOpen, Eye, Map, User, Newspaper } from 'lucide-react';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navItems = [
    { path: '/', label: 'Home', icon: Eye },
    { path: '/books', label: 'Books', icon: BookOpen },
    { path: '/cedar-falls', label: 'Cedar Falls', icon: Map },
    { path: '/author', label: 'Author', icon: User },
    { path: '/news', label: 'News', icon: Newspaper },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[70] transition-all duration-300 ${
        isScrolled
          ? 'bg-cedar-900/90 backdrop-blur-md shadow-atmospheric border-b border-cedar-700/30'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="/mk-monogram.svg"
              alt="M.K. Rivers"
              className="h-8 w-8 sm:h-10 sm:w-10 hover:opacity-90 transition-opacity duration-200"
            />
            <div className="flex flex-col">
              <span className="text-base sm:text-lg font-serif font-bold text-fog-100">
                Memory Keeper
              </span>
              <span className="text-xs text-fog-400 -mt-1">
                Mysteries
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`nav-link ${
                  location.pathname === path ? 'active text-forest-400' : ''
                }`}
              >
                {label}
              </Link>
            ))}
          </div>


          {/* Mobile menu button - Enhanced touch target */}
          <button
            className="md:hidden p-2 rounded-md text-fog-400 hover:text-fog-100 hover:bg-cedar-800/50 focus:outline-none focus:ring-2 focus:ring-forest-500 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation - Improved */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-14 sm:top-16 left-0 right-0 bg-cedar-900/95 backdrop-blur-md border-b border-cedar-700/30 z-50">
            <div className="px-3 sm:px-4 py-4 sm:py-6 space-y-3 sm:space-y-4">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center space-x-3 py-3 px-3 rounded-md transition-colors touch-manipulation min-h-[48px] ${
                    location.pathname === path
                      ? 'bg-forest-600/20 text-forest-400'
                      : 'text-fog-400 hover:text-fog-100 hover:bg-cedar-800/50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};